import { useAuth } from "../contexts/AuthContext";

// Mirrors app/core/permissions.py's ResourceArea on the backend — the
// backend is the actual enforcement point, this just drives what the UI
// shows so an Investor login doesn't have to discover restrictions via
// failed requests.
export type ResourceArea =
    | "accounts"
    | "financial_reports"
    | "orders_customers"
    | "inventory_stock"
    | "operations";

const INVESTOR_READABLE_AREAS: ResourceArea[] = [
    "financial_reports",
    "orders_customers",
    "inventory_stock",
];

export interface Permissions {
    /** Read-only login, scoped to a fixed set of areas — see canAccessArea. */
    isInvestor: boolean;
    /** Owner role — gates the handful of actions stricter than canWrite (e.g. account balance adjustments). */
    isOwner: boolean;
    /** False only for an Investor login; everyone else may write. */
    canWrite: boolean;
    /** Whether the current role may enter this area at all (read or write). */
    canAccessArea: (area: ResourceArea) => boolean;
}

export const usePermissions = (): Permissions => {
    const { user } = useAuth();
    const isInvestor = user?.role === "INVESTOR";

    return {
        isInvestor,
        isOwner: user?.role === "OWNER",
        canWrite: !isInvestor,
        canAccessArea: (area) =>
            !isInvestor || INVESTOR_READABLE_AREAS.includes(area),
    };
};
